import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/outline';
import { Dropdown, Menu } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { ShouldRender } from '../shared/basic/ShouldRender';
import Briefcase from '../../../assets/Icons/Briefcase';
import NewTextIcon from '../../../assets/Icons/NewTextIcon';
import PhoneHeaderIcon from '../../../assets/Icons/PhoneHeaderIcon';

interface NavItemProps {
  href: string;
  isActive?: boolean;
  className?: string;
  children: React.ReactNode;
}
const NavItem = ({ href, className = '', children }: NavItemProps) => {
  return (
    <motion.div
      className={clsx('flex-1 relative  ', className)}
      whileHover={{ backgroundColor: '#1a1a1a' }}
      transition={{ duration: 0.2 }}
    >
      <Link
        href={href}
        className='flex items-center justify-center px-2.5 py-[9px] text-white h-full rounded-full  border border-transparent'
      >
        <motion.div
          className='flex items-center whitespace-nowrap'
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
        >
          {children}
        </motion.div>
      </Link>
    </motion.div>
  );
};

const AnimatedNavbar = () => {
  const router = useRouter();
  const pathname = router.pathname;
  const screens = useBreakpoint();
  const [visible, setVisible] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (visible) setVisible(false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [visible]);

  if (!screens.xl) {
    const menu = (
      <Menu
        className='bg-cartBgColor border border-[#282829] w-fit rounded-xl overflow-hidden'
        style={{ width: 240 }}
        items={[
          {
            key: '1',
            label: (
              <Link href='/contact' className='text-white'>
                <div className='flex items-center py-2'>
                  <span className='pr-2'>
                    {' '}
                    <PhoneHeaderIcon />
                  </span>

                  <span className='font-medium text-sm'>Customer Support</span>
                </div>
              </Link>
            ),
          },
          {
            key: '2',
            label: (
              <Link
                href='https://business.zebralearn.com/business'
                className='text-white'
              >
                <div className='flex items-center py-2'>
                  <span className='flex items-center justify-center w-5 h-5 mr-2'>
                    <Briefcase />
                  </span>
                  <span className='font-medium pr-2 text-sm'>Bulk Order</span>
                  <span
                    style={{
                      background:
                        'linear-gradient(180deg, #F9742C 0%, #F20486 100%)',
                    }}
                    className=' px-2 py-1 text-xs font-bold text-white rounded-full'
                  >
                    <NewTextIcon />
                  </span>
                </div>
              </Link>
            ),
          },
          {
            key: '3',
            label: (
              <Link
                href='https://studio.zebralearn.com/'
                className='text-white'
              >
                <div className='flex items-center py-2'>
                  <GlobeAltIcon className='h-5 w-5 mr-2' />
                  <span className='font-medium'>Publish with us</span>
                </div>
              </Link>
            ),
          },
        ]}
      />
    );

    if (router.pathname === '/zai') {
      return <></>;
    }

    return (
      <div
        className='flex items-center justify-end space-x-2'
        ref={dropdownRef}
      >
        <Dropdown
          overlay={menu}
          trigger={[screens.lg ? 'hover' : 'click']}
          overlayClassName='dropdown-dark'
          placement='bottomRight'
          open={visible}
          onOpenChange={(v) => setVisible(v)}
        >
          <motion.div
            className={clsx(
              'flex items-center cursor-pointer border rounded-full lg:rounded-[10px]',
              'md:px-4 px-3.5 py-1.5 md:py-[7px] border-2 lg:border border-cartBgColor hover:bg-cartBgColor text-textLightGray',
            )}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              key='icon-text'
              className='flex items-center gap-2'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <ShouldRender check={screens.lg}>
                <span>
                  <PhoneHeaderIcon />
                </span>
              </ShouldRender>
              <span className='font-medium text-sm'>Contact</span>
              <span>
                <ChevronDownIcon className='w-4 h-4 font-bold' />
              </span>
            </motion.div>
          </motion.div>
        </Dropdown>
      </div>
    );
  }

  // For xl and above, show the original navbar

  if (router.pathname === '/zai') {
    return <></>;
  }

  return (
    <motion.nav
      className='w-full rounded-[10px] border-[#282829] border-[1.5px] overflow-hidden relative'
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className={`flex ${screens.sm ? 'flex-row' : 'flex-col'} relative`}>
        <NavItem
          href='https://business.zebralearn.com/business'
          isActive={pathname.includes('/business')}
          className={`${!screens.sm ? 'border-b' : 'border-b-0 border-r-[1.5px]'} border-[#282829]`}
        >
          <span className='font-medium text-sm pl-1'>Bulk Order</span>
          <span
            style={{
              background: 'linear-gradient(180deg, #F9742C 0%, #F20486 100%)',
            }}
            className='ml-1 px-2 py-1 text-xs font-bold text-white rounded-full'
          >
            <NewTextIcon />
          </span>
        </NavItem>

        <NavItem
          href='https://studio.zebralearn.com/'
          isActive={pathname.includes('/publish')}
          className={`${!screens.sm ? 'border-b' : 'border-b-0 border-r-[1.5px]'} border-[#282829]`}
        >
          <GlobeAltIcon className='mr-2 h-5 w-5' />
          <span className='font-medium text-sm pr-1'>Publish with us</span>
        </NavItem>

        <NavItem href='/contact' isActive={pathname.includes('/contact')}>
          <span className='mr-2'>
            <PhoneHeaderIcon />
          </span>
          <span className='font-medium text-sm pr-1'>Contact</span>
        </NavItem>
      </div>
    </motion.nav>
  );
};

export default AnimatedNavbar;
